/**
 * Mobile summary sheet for the /map page — the thumb-reachable version of the
 * marker summary card. On narrow screens the desktop panels (MapRightPanel /
 * MapBottomPanel) are hidden and a Leaflet popup is too cramped to read, so
 * tapping a marker opens this fixed bottom sheet instead: grade, national
 * rank, price, quality signals, and the two actions that matter (full prices,
 * directions). Rows render only when the data exists.
 */
import React from "react";
import { useTranslation } from "react-i18next";
import { X, Navigation, ArrowRight } from "lucide-react";
import { gradeColor } from "@/lib/grades";
import { titleCaseHospital } from "@/lib/hospitalName";
import { ownershipBucket } from "@/lib/mapFilters";

export interface SheetHospital {
  hospital_id: string;
  hospital_name: string;
  latitude: number;
  longitude: number;
  city?: string;
  state?: string;
  grade?: string | null;
  rank?: number | null;
  scenarioPrice?: number | null;
  cmsStars?: number | null;
  cmsSafety?: string | null;
  cmsMortality?: string | null;
  cmsReadmission?: string | null;
  cmsExperience?: string | null;
  googleRating?: number | null;
  googleReviews?: number | null;
  ownership?: string | null;
  bedCount?: number | null;
  emergencyServices?: boolean | null;
  hospitalSystem?: string | null;
}

interface Props {
  hospital: SheetHospital;
  totalRanked?: number | null;
  scenarioMode?: boolean;
  dark: boolean;
  onClose: () => void;
  /** Bottom offset (px) so the sheet clears the tab bar. */
  bottomOffset?: number;
}

const OWNERSHIP_LABEL: Record<string, string> = {
  nonprofit: "Nonprofit",
  forprofit: "For-profit",
  government: "Government",
};

function qualDir(v: string | null | undefined): { sym: string; color: string } | null {
  if (!v) return null;
  const s = v.toLowerCase();
  if (s.startsWith("above")) return { sym: "↑", color: "#16a34a" };
  if (s.startsWith("below")) return { sym: "↓", color: "#dc2626" };
  if (s.startsWith("same") || s.startsWith("no different")) return { sym: "=", color: "#9ca3af" };
  return null;
}

export const MobileHospitalSheet: React.FC<Props> = ({
  hospital: h, totalRanked, scenarioMode, dark, onClose, bottomOffset = 88,
}) => {
  const { t } = useTranslation("map");
  const bg = dark ? "#0f1d2f" : "#ffffff";
  const border = dark ? "rgba(197,165,90,0.35)" : "rgba(0,40,104,0.15)";
  const ink = dark ? "#f0ebe3" : "#1B1B1B";
  const muted = dark ? "rgba(240,235,227,0.65)" : "rgba(27,27,27,0.6)";
  const accent = dark ? "#C5A55A" : "#002868";

  const ownBucket = ownershipBucket(h.ownership);
  const factBits: string[] = [];
  if (OWNERSHIP_LABEL[ownBucket]) factBits.push(OWNERSHIP_LABEL[ownBucket]);
  if (h.bedCount) factBits.push(`${h.bedCount.toLocaleString()} ${t("sheet.beds", { defaultValue: "beds" })}`);
  if (h.emergencyServices === true) factBits.push(t("sheet.hasEr", { defaultValue: "ER ✓" }));
  else if (h.emergencyServices === false) factBits.push(t("sheet.noEr", { defaultValue: "No ER" }));

  const starBits: string[] = [];
  if (h.cmsStars != null) starBits.push(`CMS ${"★".repeat(Math.round(h.cmsStars))}${"☆".repeat(5 - Math.round(h.cmsStars))}`);
  if (h.googleRating != null) starBits.push(`Google ${h.googleRating.toFixed(1)}${h.googleReviews ? ` (${h.googleReviews.toLocaleString()})` : ""}`);

  const qualBits = ([
    [t("sheet.safety", { defaultValue: "Safety" }), qualDir(h.cmsSafety)],
    [t("sheet.mortality", { defaultValue: "Mortality" }), qualDir(h.cmsMortality)],
    [t("sheet.readmits", { defaultValue: "Readmits" }), qualDir(h.cmsReadmission)],
    [t("sheet.experience", { defaultValue: "Experience" }), qualDir(h.cmsExperience)],
  ] as const).filter((q): q is [string, { sym: string; color: string }] => q[1] != null);

  return (
    <div
      role="dialog"
      aria-label={h.hospital_name}
      style={{
        position: "fixed", left: 0, right: 0, zIndex: 260,
        bottom: `calc(${bottomOffset}px + env(safe-area-inset-bottom, 0px))`,
        margin: "0 8px",
        backgroundColor: bg, color: ink,
        border: `1px solid ${border}`, borderRadius: 16,
        boxShadow: "0 -4px 24px rgba(0,0,0,0.35)",
        padding: "10px 14px 12px",
        animation: "mhs-up 0.22s ease-out",
      }}
    >
      <style>{`@keyframes mhs-up { from { transform: translateY(24px); opacity: 0.4; } to { transform: none; opacity: 1; } }`}</style>
      {/* Grab handle + close */}
      <div style={{ display: "flex", justifyContent: "center", position: "relative", marginBottom: 6 }}>
        <div style={{ width: 34, height: 4, borderRadius: 4, backgroundColor: dark ? "rgba(255,255,255,0.2)" : "rgba(27,27,27,0.15)" }} />
        <button
          type="button" onClick={onClose} aria-label={t("sheet.close", { defaultValue: "Close" })}
          style={{ position: "absolute", right: -6, top: -4, padding: 8, background: "none", border: "none", color: muted, cursor: "pointer" }}
        >
          <X style={{ width: 17, height: 17 }} />
        </button>
      </div>

      {/* Identity row */}
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
        {h.grade && (
          <span style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            width: 34, height: 34, borderRadius: 17, backgroundColor: gradeColor(h.grade),
            color: "#fff", fontSize: 17, fontWeight: 800, marginTop: 1,
            border: "2px solid rgba(255,255,255,0.85)", boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
          }}>{h.grade}</span>
        )}
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{
            fontWeight: 700, fontSize: 15, lineHeight: 1.25,
            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as any, overflow: "hidden",
          }}>{titleCaseHospital(h.hospital_name)}</div>
          <div style={{ fontSize: 12, color: muted, marginTop: 1 }}>
            {h.rank != null && (
              <span style={{ fontWeight: 700, color: ink }}>
                #{h.rank.toLocaleString()}{totalRanked ? ` ${t("sheet.of", { defaultValue: "of" })} ${totalRanked.toLocaleString()}` : ""}
              </span>
            )}
            {h.rank != null && (h.city || h.state) ? " · " : ""}
            {[h.city, h.state].filter(Boolean).join(", ")}
          </div>
        </div>
        {h.scenarioPrice != null && h.scenarioPrice > 0 && (
          <div style={{ textAlign: "right", flexShrink: 0 }}>
            <div style={{ fontSize: 19, fontWeight: 800, color: accent, lineHeight: 1 }}>
              ${Math.round(h.scenarioPrice).toLocaleString()}
            </div>
            <div style={{ fontSize: 9, fontWeight: 600, color: muted, marginTop: 2 }}>
              {t("sheet.thisScenario", { defaultValue: "this scenario" })}
            </div>
          </div>
        )}
      </div>

      {/* Signals */}
      {(starBits.length > 0 || qualBits.length > 0 || factBits.length > 0 || h.hospitalSystem) && (
        <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 3 }}>
          {starBits.length > 0 && <div style={{ fontSize: 12, color: ink }}>{starBits.join(" · ")}</div>}
          {qualBits.length > 0 && (
            <div style={{ fontSize: 11, color: muted }}>
              {qualBits.map(([label, d], i) => (
                <span key={label}>
                  {i > 0 && " · "}
                  {label} <span style={{ color: d.color, fontWeight: 800 }}>{d.sym}</span>
                </span>
              ))}
              <span style={{ opacity: 0.7 }}> {t("sheet.vsNatl", { defaultValue: "vs natl avg" })}</span>
            </div>
          )}
          {factBits.length > 0 && <div style={{ fontSize: 11, color: muted }}>{factBits.join(" · ")}</div>}
          {h.hospitalSystem && (
            <div style={{ fontSize: 10, color: muted }}>
              {t("sheet.partOf", { defaultValue: "Part of" })} {h.hospitalSystem}
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <a
          href={`/hospital/${h.hospital_id}`}
          style={{
            flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
            padding: "10px 12px", borderRadius: 10, textDecoration: "none",
            backgroundColor: accent, color: dark ? "#0f1d2f" : "#fff",
            fontSize: 13, fontWeight: 700,
          }}
        >
          {t("sheet.viewPrices", { defaultValue: "View full prices" })}
          <ArrowRight style={{ width: 14, height: 14 }} />
        </a>
        <a
          href={`https://maps.google.com/?q=${h.latitude},${h.longitude}`}
          target="_blank" rel="noopener noreferrer"
          aria-label={t("sheet.directions", { defaultValue: "Directions" })}
          style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
            padding: "10px 14px", borderRadius: 10, textDecoration: "none",
            backgroundColor: "transparent", color: accent,
            border: `1.5px solid ${accent}`, fontSize: 13, fontWeight: 700,
          }}
        >
          <Navigation style={{ width: 14, height: 14 }} />
          {t("sheet.directions", { defaultValue: "Directions" })}
        </a>
      </div>
    </div>
  );
};
